import React, { useState, useCallback } from 'react';
import { validateEmail } from '../../utils/validation';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

export default function ForwardCaptureForm({ intentCode, forwardCapture, onComplete }) {
  const [formData, setFormData] = useState({ email: '', firstName: '', lastName: '' });
  const [state, setState] = useState('form');
  const [emailError, setEmailError] = useState(null);
  const [result, setResult] = useState(null);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'email' && emailError) {
      setEmailError(null);
    }
  }, [emailError]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const trimmedEmail = formData.email.trim();
    if (!trimmedEmail || !validateEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setState('submitting');
    setEmailError(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/marketing/intent-forward-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intentCode,
          email: trimmedEmail,
          firstName: formData.firstName.trim() || undefined,
          lastName: formData.lastName.trim() || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 422 && data.error === 'INVALID_EMAIL') {
          setEmailError('Please enter a valid email address');
          setState('form');
          return;
        }
        setState('form');
        setEmailError('Something went wrong. Please try again.');
        return;
      }

      const data = await res.json();
      if (onComplete) {
        onComplete(data);
        return;
      }
      setResult(data);
      setState('done');
    } catch {
      setState('form');
      setEmailError('Something went wrong. Please try again.');
    }
  }, [formData, intentCode, onComplete]);

  const handleDismiss = useCallback(() => {
    setState('dismissed');
  }, []);

  if (state === 'dismissed') {
    const dismissMsg = forwardCapture?.dismissMessage || "We understand. It's too soon.\nBrowse the site and see if we are a good fit.";
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        {dismissMsg.split('\n').map((line, i) => (
          <p key={i} className="text-gray-700 mb-2 text-lg font-medium">{line}</p>
        ))}
        {forwardCapture?.ctaText && forwardCapture?.ctaUrl && (
          <a
            href={forwardCapture.ctaUrl}
            className="inline-block mt-4 px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors min-h-[44px]"
          >
            {forwardCapture.ctaText}
          </a>
        )}
      </div>
    );
  }

  if (state === 'done' && result) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">
          {result.displayContent?.heading || 'Thanks for your interest!'}
        </h1>
        {result.displayContent?.body && (
          <p className="text-gray-600">{result.displayContent.body}</p>
        )}
      </div>
    );
  }

  const heading = forwardCapture?.heading || "Someone thought you'd find this interesting — that's fantastic!";
  const body = forwardCapture?.body || 'Drop your email and name to stay in the loop.';
  const isSubmitting = state === 'submitting';

  return (
    <div className="bg-white rounded-lg shadow-sm p-8">
      <h1 className="text-xl font-semibold text-gray-900 mb-2 text-center">{heading}</h1>
      <p className="text-gray-600 mb-6 text-center">{body}</p>

      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-4">
          <label htmlFor="fc-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="fc-email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px] ${
              emailError ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="you@example.com"
          />
          {emailError && (
            <p className="mt-1 text-sm text-red-600" role="alert">{emailError}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="fc-firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First name
            </label>
            <input
              id="fc-firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px]"
            />
          </div>
          <div>
            <label htmlFor="fc-lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last name
            </label>
            <input
              id="fc-lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px]"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors min-h-[44px] disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : 'Stay in the loop'}
        </button>
      </form>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={handleDismiss}
          disabled={isSubmitting}
          className="text-sm text-gray-500 hover:text-gray-700 underline min-h-[44px]"
        >
          No thanks
        </button>
      </div>
    </div>
  );
}
